import React, { createContext, useContext, useState, useEffect } from 'react';
import { storageKeys } from 'config/app.config';
import { Store } from 'dtos/org/store.dto';
import { Organization } from 'dtos/org/org.dto';
import { apiClient } from 'services/api-client';

interface StoreContextValue {
  org: Organization | null;
  stores: Store[];
  currentStore: Store | null;
  setCurrentStore: (store: Store) => void;
  refresh: () => Promise<void>;
  loading: boolean;
  error: string | null;
}

const StoreContext = createContext<StoreContextValue>(null as any);

export function StoreProvider({ children }: { children: React.ReactNode }) {
  const [org, setOrg] = useState<Organization | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [currentStore, setCurrentStoreState] = useState<Store | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const organizations = await apiClient.get<Organization[]>('/me/organizations');
      const selectedOrg = organizations.data[0] ?? null;
      setOrg(selectedOrg);
      if (!selectedOrg) { setStores([]); setCurrentStoreState(null); return; }
      const storeResponse = await apiClient.get<Store[]>(`/orgs/${selectedOrg.id}/stores`);
      const availableStores = storeResponse.data;
      setStores(availableStores);
      const savedId = localStorage.getItem(storageKeys.currentStoreId);
      const found = availableStores.find(s => s.id === savedId) ?? availableStores[0] ?? null;
      setCurrentStoreState(found);
    } catch (contextError: any) {
      setError(contextError?.message || 'Unable to load organization context.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const setCurrentStore = (store: Store) => {
    localStorage.setItem(storageKeys.currentStoreId, store.id);
    setCurrentStoreState(store);
  };

  return (
    <StoreContext.Provider value={{ org, stores, currentStore, setCurrentStore, refresh, loading, error }}>
      {children}
    </StoreContext.Provider>
  );
}

export const useStore = () => useContext(StoreContext);

